import type {AxiosResponse} from "axios";

import CoreApi from "@/src/modules/core/api";
import {axios, getParams} from "@/src/modules/core/lib";
import type {GetListResponse, GetResponse, ParamsOptions} from "@/src/modules/core/types";

import type {
  DisciplinaryActionPayload,
  DisciplinaryHistoryItem,
  IncidentCreatePayload,
  IncidentHistoryItem,
  OperationalHistoryItem,
  PerformanceHistoryItem,
  PerformanceReviewCreatePayload,
  RecordActionRequest,
  RecordActionResponse,
  RecordDomain,
  TrainingHistoryItem,
  TrainingRecordCreatePayload,
  LeavePoliciesPayload,
  LeavePolicy,
  LeavePolicyUpdatePayload,
  LeaveRequest,
  LeaveRequestPayload,
  LeaveRequestSubmitResponse,
  OfficerContact,
  OfficerContactPayload,
  CreateOfficerProfileRequest,
  OfficerProfile,
  LicenseComplianceRow,
  SecurityLicense,
  SecurityLicensePayload,
  SecurityLicenseReportItem,
  SecurityLicenseType,
  SecurityLicenseUpdatePayload,
  LiftWorkAuthorizationPayload,
  ReactivateOfficerProfileRequest,
  TerminateOfficerProfileRequest,
  UpdateOfficerProfileRequest,
  WorkAuthorization,
  WorkAuthorizationPayload,
  WorkEligibility,
} from "../types";

class EmployeeHrApi extends CoreApi {
  getSelfProfile(): Promise<AxiosResponse<GetResponse<OfficerProfile>>> {
    return this.get("employee-profile");
  }

  getSelfOperationalHistory(
    options: ParamsOptions = {},
  ): Promise<AxiosResponse<{success: number; data: OperationalHistoryItem[]}>> {
    return axios({
      url: "employee-profile/operational-history",
      method: "get",
      params: getParams(options),
    });
  }

  getSelfOperationalHistoryRecord(
    recordId: number,
  ): Promise<AxiosResponse<{success: number; data: OperationalHistoryItem}>> {
    return axios({
      url: `employee-profile/operational-history/${recordId}`,
      method: "get",
    });
  }

  getOfficerOperationalHistory(
    officerProfileId: number,
    options: ParamsOptions = {},
  ): Promise<AxiosResponse<{success: number; data: OperationalHistoryItem[]}>> {
    return axios({
      url: `employee-profiles/${officerProfileId}/operational-history`,
      method: "get",
      params: getParams(options),
    });
  }

  getOfficerOperationalHistoryRecord(
    officerProfileId: number,
    recordId: number,
  ): Promise<AxiosResponse<{success: number; data: OperationalHistoryItem}>> {
    return axios({
      url: `employee-profiles/${officerProfileId}/operational-history/${recordId}`,
      method: "get",
    });
  }

  postSelfRecordAction(
    domain: RecordDomain,
    recordId: number,
    body: RecordActionRequest,
  ): Promise<AxiosResponse<{success: number; data: RecordActionResponse}>> {
    return axios({
      url: `employee-profile/records/${domain}/${recordId}/actions`,
      method: "post",
      data: body,
    });
  }

  postOfficerRecordAction(
    officerProfileId: number,
    domain: RecordDomain,
    recordId: number,
    body: RecordActionRequest,
  ): Promise<AxiosResponse<{success: number; data: RecordActionResponse}>> {
    return axios({
      url: `employee-profiles/${officerProfileId}/records/${domain}/${recordId}/actions`,
      method: "post",
      data: body,
    });
  }

  getDisciplinaryHistory(officerProfileId: number) {
    return axios({
      url: `employee-profiles/${officerProfileId}/disciplinary-history`,
      method: "get",
    });
  }

  getIncidentHistory(officerProfileId: number) {
    return axios({
      url: `employee-profiles/${officerProfileId}/incident-history`,
      method: "get",
    });
  }

  getTrainingHistory(officerProfileId: number) {
    return axios({
      url: `employee-profiles/${officerProfileId}/training-history`,
      method: "get",
    });
  }

  getPerformanceHistory(officerProfileId: number) {
    return axios({
      url: `employee-profiles/${officerProfileId}/performance-history`,
      method: "get",
    });
  }

  createDisciplinaryAction(officerProfileId: number, data: DisciplinaryActionPayload) {
    return this.create(`employee-profiles/${officerProfileId}/disciplinary-actions`, data);
  }

  createOfficerIncident(officerProfileId: number, data: IncidentCreatePayload) {
    return this.create(`employee-profiles/${officerProfileId}/incidents`, data);
  }

  createTrainingRecord(officerProfileId: number, data: TrainingRecordCreatePayload) {
    return this.create(`employee-profiles/${officerProfileId}/training-records`, data);
  }

  createPerformanceReview(officerProfileId: number, data: PerformanceReviewCreatePayload) {
    return this.create(`employee-profiles/${officerProfileId}/performance-reviews`, data);
  }

  getSelfContacts(): Promise<AxiosResponse<GetListResponse<OfficerContact>>> {
    return this.get("employee-contacts");
  }

  createSelfContact(data: OfficerContactPayload): Promise<AxiosResponse<GetResponse<OfficerContact>>> {
    return this.create("employee-contacts", data);
  }

  updateSelfContact(
    contactId: number,
    data: Partial<OfficerContactPayload>,
  ): Promise<AxiosResponse<GetResponse<OfficerContact>>> {
    return this.update("employee-contacts", contactId, data);
  }

  deleteSelfContact(contactId: number): Promise<AxiosResponse<void>> {
    return this.delete("employee-contacts", contactId);
  }

  getOfficerContacts(officerProfileId: number): Promise<AxiosResponse<GetListResponse<OfficerContact>>> {
    return this.get(`employee-profiles/${officerProfileId}/contacts`);
  }

  createOfficerContact(
    officerProfileId: number,
    data: OfficerContactPayload,
  ): Promise<AxiosResponse<GetResponse<OfficerContact>>> {
    return this.create(`employee-profiles/${officerProfileId}/contacts`, data);
  }

  updateOfficerContact(
    officerProfileId: number,
    contactId: number,
    data: Partial<OfficerContactPayload>,
  ): Promise<AxiosResponse<GetResponse<OfficerContact>>> {
    return this.update(`employee-profiles/${officerProfileId}/contacts`, contactId, data);
  }

  deleteOfficerContact(officerProfileId: number, contactId: number): Promise<AxiosResponse<void>> {
    return this.delete(`employee-profiles/${officerProfileId}/contacts`, contactId);
  }

  getSelfLeaveRequests(options: ParamsOptions = {}): Promise<AxiosResponse<GetListResponse<LeaveRequest>>> {
    return axios({
      url: "leave/requests",
      method: "get",
      params: getParams(options),
    });
  }

  createSelfLeaveRequest(
    data: LeaveRequestPayload,
  ): Promise<AxiosResponse<GetResponse<LeaveRequestSubmitResponse>>> {
    return this.create("leave/requests", data);
  }

  cancelSelfLeaveRequest(leaveRequestId: number): Promise<AxiosResponse<GetResponse<LeaveRequest>>> {
    return this.post(`leave/requests/${leaveRequestId}/cancel`, {});
  }

  createOfficerLeaveRequest(
    officerProfileId: number,
    data: LeaveRequestPayload,
  ): Promise<AxiosResponse<GetResponse<LeaveRequestSubmitResponse>>> {
    return this.create(`employee-profiles/${officerProfileId}/leave/requests`, data);
  }

  getLeaves(options: ParamsOptions = {}): Promise<AxiosResponse<GetListResponse<LeaveRequest>>> {
    return axios({
      url: "leaves",
      method: "get",
      params: getParams(options),
    });
  }

  approveLeaveRequest(leaveRequestId: number): Promise<AxiosResponse<GetResponse<LeaveRequest>>> {
    return this.post(`leave/requests/${leaveRequestId}/approve`, {});
  }

  denyLeaveRequest(leaveRequestId: number): Promise<AxiosResponse<GetResponse<LeaveRequest>>> {
    return this.post(`leave/requests/${leaveRequestId}/deny`, {});
  }

  getOfficerProfiles(options: ParamsOptions = {}): Promise<AxiosResponse<GetListResponse<OfficerProfile>>> {
    return axios({
      url: "employee-profiles",
      method: "get",
      params: getParams(options),
    });
  }

  getOfficerProfile(officerProfileId: number): Promise<AxiosResponse<GetResponse<OfficerProfile>>> {
    return this.getById("employee-profiles", officerProfileId);
  }

  createOfficerProfile(
    data: CreateOfficerProfileRequest,
  ): Promise<AxiosResponse<GetResponse<OfficerProfile>>> {
    return this.create("employee-profiles", data);
  }

  updateOfficerProfile(
    officerProfileId: number,
    data: UpdateOfficerProfileRequest | Partial<OfficerProfile>,
  ): Promise<AxiosResponse<GetResponse<OfficerProfile>>> {
    return this.update("employee-profiles", officerProfileId, data);
  }

  terminateOfficerProfile(
    officerProfileId: number,
    data: TerminateOfficerProfileRequest,
  ): Promise<AxiosResponse<GetResponse<OfficerProfile>>> {
    return this.post(`employee-profiles/${officerProfileId}/terminate`, data);
  }

  reactivateOfficerProfile(
    officerProfileId: number,
    data: ReactivateOfficerProfileRequest,
  ): Promise<AxiosResponse<GetResponse<OfficerProfile>>> {
    return this.post(`employee-profiles/${officerProfileId}/reactivate`, data);
  }

  getSecurityLicenses(
    officerProfileId: number,
  ): Promise<AxiosResponse<GetListResponse<SecurityLicense>>> {
    return this.get(`employee-profiles/${officerProfileId}/security-licenses`);
  }

  createSecurityLicense(
    officerProfileId: number,
    data: SecurityLicensePayload,
  ): Promise<AxiosResponse<GetResponse<SecurityLicense>>> {
    return this.create(`employee-profiles/${officerProfileId}/security-licenses`, data);
  }

  updateSecurityLicense(
    officerProfileId: number,
    licenseId: number,
    data: SecurityLicenseUpdatePayload,
  ): Promise<AxiosResponse<GetResponse<SecurityLicense>>> {
    return axios({
      url: `employee-profiles/${officerProfileId}/security-licenses/${licenseId}`,
      method: "patch",
      data,
    });
  }

  getSecurityLicenseTypes(): Promise<AxiosResponse<GetListResponse<SecurityLicenseType>>> {
    return this.get("security-license-types");
  }

  getOfficerWorkEligibility(
    officerProfileId: number,
  ): Promise<AxiosResponse<GetResponse<WorkEligibility>>> {
    return this.get(`employee-profiles/${officerProfileId}/work-eligibility`);
  }

  getSelfWorkEligibility(): Promise<AxiosResponse<GetResponse<WorkEligibility>>> {
    return this.get("employee-profile/work-eligibility");
  }

  getOfficerWorkAuthorizations(
    officerProfileId: number,
  ): Promise<AxiosResponse<GetListResponse<WorkAuthorization>>> {
    return this.get(`employee-profiles/${officerProfileId}/work-authorizations`);
  }

  createOfficerWorkAuthorization(
    officerProfileId: number,
    data: WorkAuthorizationPayload,
  ): Promise<AxiosResponse<GetResponse<WorkAuthorization>>> {
    return this.create(`employee-profiles/${officerProfileId}/work-authorizations`, data);
  }

  liftOfficerWorkAuthorization(
    officerProfileId: number,
    authorizationId: number,
    data: LiftWorkAuthorizationPayload,
  ): Promise<AxiosResponse<GetResponse<WorkAuthorization>>> {
    return axios({
      url: `employee-profiles/${officerProfileId}/work-authorizations/${authorizationId}/lift`,
      method: "post",
      data,
    });
  }

  getLicenseCompliance(
    options: ParamsOptions = {},
  ): Promise<AxiosResponse<GetListResponse<LicenseComplianceRow>>> {
    return axios({
      url: "employee-profiles/license-compliance",
      method: "get",
      params: getParams(options),
    });
  }

  getExpiringSecurityLicenses(
    options: ParamsOptions = {},
  ): Promise<AxiosResponse<GetListResponse<SecurityLicenseReportItem>>> {
    return axios({
      url: "employee-profiles/expiring-security-licenses",
      method: "get",
      params: getParams(options),
    });
  }

  getCompanyLeavePolicies(companyId: number): Promise<AxiosResponse<{data: LeavePolicy[]}>> {
    return this.get(`companies/${companyId}/leave-policies`);
  }

  upsertCompanyLeavePolicies(
    companyId: number,
    data: LeavePoliciesPayload,
  ): Promise<AxiosResponse<{data: LeavePolicy[]}>> {
    return axios({
      url: `companies/${companyId}/leave-policies`,
      method: "put",
      data,
    });
  }

  updateCompanyLeavePolicy(
    companyId: number,
    policyId: number,
    data: LeavePolicyUpdatePayload,
  ): Promise<AxiosResponse<{data: LeavePolicy}>> {
    return axios({
      url: `companies/${companyId}/leave-policies/${policyId}`,
      method: "patch",
      data,
    });
  }
}

export const employeeHrApi = new EmployeeHrApi();
